import React, { useMemo, useState } from 'react';
import {
  CalendarCheck,
  CheckCheck,
  ChevronRight,
  ClipboardList,
  FileText,
  UserPlus,
} from 'lucide-react';
import { SiteActivityItem, SiteActivityKind } from '../types';
import { OperationsAlerts } from '../lib/operationsAlerts';

type Filter = 'ALL' | 'ACCOUNTS' | 'ESTIMATES' | 'SITE';

interface Props {
  alerts: OperationsAlerts;
  onNavigate?: (tab: string) => void;
}

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'ALL', label: 'All' },
  { id: 'ACCOUNTS', label: 'Accounts' },
  { id: 'ESTIMATES', label: 'Estimates' },
  { id: 'SITE', label: 'Site' },
];

const KIND_STYLE: Record<SiteActivityKind, { icon: React.ReactNode; tile: string; text: string }> = {
  ACCOUNT: {
    icon: <UserPlus className="w-4 h-4" />,
    tile: 'bg-violet-100 text-violet-700',
    text: 'text-violet-700',
  },
  ESTIMATE_REQUEST: {
    icon: <FileText className="w-4 h-4" />,
    tile: 'bg-amber-100 text-amber-700',
    text: 'text-amber-700',
  },
  BOOKING: {
    icon: <CalendarCheck className="w-4 h-4" />,
    tile: 'bg-emerald-100 text-emerald-700',
    text: 'text-emerald-700',
  },
  WORKSHEET: {
    icon: <ClipboardList className="w-4 h-4" />,
    tile: 'bg-teal-100 text-teal-700',
    text: 'text-teal-700',
  },
};

function kindMatches(kind: SiteActivityKind, filter: Filter): boolean {
  if (filter === 'ALL') return true;
  if (filter === 'ACCOUNTS') return kind === 'ACCOUNT';
  if (filter === 'ESTIMATES') return kind === 'ESTIMATE_REQUEST' || kind === 'BOOKING';
  return kind === 'WORKSHEET';
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms) || ms < 0) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const ActivityAlertBox: React.FC<Props> = ({ alerts, onNavigate }) => {
  const [filter, setFilter] = useState<Filter>('ALL');
  const unread = new Set(alerts.unreadActivityIds);

  const counts = useMemo(() => {
    const account = alerts.activity.filter(item => item.kind === 'ACCOUNT');
    const estimates = alerts.activity.filter(item => item.kind === 'ESTIMATE_REQUEST' || item.kind === 'BOOKING');
    const site = alerts.activity.filter(item => item.kind === 'WORKSHEET');
    const unreadCount = (items: SiteActivityItem[]) => items.filter(item => unread.has(item.id)).length;
    return {
      account: account.length,
      accountUnread: unreadCount(account),
      estimates: estimates.length,
      estimateUnread: unreadCount(estimates),
      site: site.length,
      siteUnread: unreadCount(site),
      newAccountsToday: account.filter(item => Date.now() - new Date(item.createdAt).getTime() < 86_400_000).length,
      estimatesToday: estimates.filter(item => Date.now() - new Date(item.createdAt).getTime() < 86_400_000).length,
    };
  }, [alerts.activity, alerts.unreadActivityIds]);

  const visible = alerts.activity.filter(item => kindMatches(item.kind, filter)).slice(0, 24);

  const openItem = (item: SiteActivityItem) => {
    alerts.markActivityRead(item.id);
    if (item.route) onNavigate?.(item.route);
  };

  return (
    <section className="bg-white rounded-[32px] border border-stone-200 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="p-6 pb-4 border-b border-stone-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gecko opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gecko" />
              </span>
              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-stone-400">Site Activity</p>
            </div>
            <h3 className="text-lg font-black text-bark mt-1">Activity alerts</h3>
            <p className="text-[11px] font-medium text-stone-500 mt-1">
              New accounts, estimate requests and other website interaction.
            </p>
          </div>
          {alerts.unreadActivityIds.length > 0 && (
            <span className="min-w-[1.75rem] h-7 px-2 rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center flex-shrink-0">
              {alerts.unreadActivityIds.length}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <button
            onClick={() => setFilter('ACCOUNTS')}
            className={`text-left p-3 rounded-2xl border transition ${
              filter === 'ACCOUNTS' ? 'border-violet-300 bg-violet-50' : 'border-stone-200 bg-stone-50 hover:bg-violet-50/60'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="w-7 h-7 rounded-xl bg-white text-violet-600 flex items-center justify-center shadow-sm">
                <UserPlus className="w-3.5 h-3.5" />
              </span>
              {counts.accountUnread > 0 && (
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-label={`${counts.accountUnread} unread`} />
              )}
            </div>
            <p className="text-2xl font-black text-bark mt-2 leading-none">{counts.newAccountsToday}</p>
            <p className="text-[9px] font-black uppercase tracking-widest text-stone-400 mt-1">New accounts · 24h</p>
          </button>

          <button
            onClick={() => setFilter('ESTIMATES')}
            className={`text-left p-3 rounded-2xl border transition ${
              filter === 'ESTIMATES' ? 'border-amber-300 bg-amber-50' : 'border-stone-200 bg-stone-50 hover:bg-amber-50/60'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="w-7 h-7 rounded-xl bg-white text-amber-600 flex items-center justify-center shadow-sm">
                <FileText className="w-3.5 h-3.5" />
              </span>
              {counts.estimateUnread > 0 && (
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-label={`${counts.estimateUnread} unread`} />
              )}
            </div>
            <p className="text-2xl font-black text-bark mt-2 leading-none">{counts.estimatesToday}</p>
            <p className="text-[9px] font-black uppercase tracking-widest text-stone-400 mt-1">Estimate requests · 24h</p>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1 mt-4">
          {FILTERS.map(item => (
            <button
              key={item.id}
              onClick={() => setFilter(item.id)}
              className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider whitespace-nowrap transition ${
                filter === item.id
                  ? 'bg-bark text-white'
                  : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
              }`}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={alerts.markAllActivityRead}
            disabled={alerts.unreadActivityIds.length === 0}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1.5 text-[9px] font-black uppercase tracking-wider text-gecko hover:bg-gecko/10 rounded-lg transition disabled:opacity-40 disabled:hover:bg-transparent whitespace-nowrap"
          >
            <CheckCheck className="w-3 h-3" /> Read all
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-1.5" style={{ maxHeight: 420 }}>
        {visible.length === 0 && (
          <div className="py-10 px-4 text-center">
            <ClipboardList className="w-7 h-7 text-stone-300 mx-auto" />
            <p className="text-xs font-black text-stone-500 mt-3">No activity in this view yet</p>
            <p className="text-[10px] text-stone-400 mt-1">
              New sign-ups, estimate requests and public form submissions land here in real time.
            </p>
          </div>
        )}

        {visible.map(item => {
          const style = KIND_STYLE[item.kind];
          const isUnread = unread.has(item.id);
          return (
            <button
              key={item.id}
              onClick={() => openItem(item)}
              className={`w-full text-left flex items-start gap-3 p-2.5 rounded-2xl border transition group ${
                isUnread
                  ? 'bg-white border-stone-200 hover:border-gecko/40 shadow-sm'
                  : 'bg-stone-50/60 border-transparent hover:bg-stone-100/70'
              }`}
            >
              <span className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${style.tile}`}>
                {style.icon}
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-start justify-between gap-2">
                  <span className={`text-[11px] font-black leading-tight ${isUnread ? 'text-bark' : 'text-stone-600'} line-clamp-2`}>
                    {item.title}
                  </span>
                  {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 mt-1" />}
                </span>
                <span className="block text-[10px] font-medium text-stone-400 truncate mt-0.5">{item.detail}</span>
                <span className="block text-[9px] font-bold text-stone-400 uppercase tracking-wider mt-1">
                  {timeAgo(item.createdAt)}
                </span>
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-stone-300 group-hover:text-gecko transition flex-shrink-0 mt-2" />
            </button>
          );
        })}
      </div>

      <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between gap-3">
        <p className="text-[9px] font-bold text-stone-400 uppercase tracking-wider">
          {counts.account + counts.estimates + counts.site} events · last 14 days
        </p>
        <button
          onClick={() => onNavigate?.('leads')}
          className="text-[9px] font-black text-gecko uppercase tracking-widest hover:underline"
        >
          Open CRM
        </button>
      </div>
    </section>
  );
};

export default ActivityAlertBox;
